'use client';

import { Modal, type ModalHandle, type ModalProps } from '@lfg/ui/components';
import { IconButton, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { icons } from '@/assets/icons';
import { useGA } from '@/hooks/useGA';

import HexagonalButton from './HexagonalButton';

export interface DiscordModalHandle extends ModalHandle {}
export interface DiscordModalProps extends Omit<ModalProps, 'children'> {}

const DiscordModal = forwardRef<DiscordModalHandle, DiscordModalProps>(({ ...rest }, ref) => {
  const modalRef = useRef<ModalHandle>(null);
  const { trackButtonClickEvent } = useGA();

  const onShow = useCallback(() => {
    modalRef.current?.show();
  }, [modalRef.current]);

  const onHide = useCallback(() => {
    modalRef.current?.hide();
    sessionStorage.setItem('discordModal', 'true');
  }, [modalRef.current]);

  useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

  const openDiscord = () => {
    trackButtonClickEvent({ name: 'join_discord' });
    window.open('https://discord.gg/PjTfVJh4Wr', '_blank');
    onHide();
  };

  return (
    <Modal ref={modalRef} bgColor={'#060912'} onClose={onHide} {...rest}>
      <Stack gap={3} flexGrow={1} alignItems={'center'} position={'relative'}>
        <Stack position={'absolute'} top={-10} right={-10} zIndex={1000}>
          <IconButton onClick={onHide} sx={{ padding: 0 }}>
            <Image width={24} height={24} src={icons.ic_x} alt='' />
          </IconButton>
        </Stack>
        <Stack alignItems={'center'} gap={3} direction={'row'}>
          <Image width={78} height={78} src={'/logo.png'} alt={'lfg'} />
          <Image width={48} height={5} src={icons.ic_3_dots} alt={''} />
          <Image width={78} height={78} src={'/icons/socials/discord.svg'} alt={'discord'} />
        </Stack>
        <Stack gap={2}>
          <Typography variant={'headLarge1'} fontSize={'16px'} fontWeight={'500'} textAlign={'center'} color={'white'}>
            Earn Rewards for Joining and Being Part of the LFG Inc. Discord Community!
          </Typography>
          <Typography variant={'bodyMedium'} fontSize={'14px'} color={'white'} whiteSpace={'pre-line'}>
            {`Be part of the Web3 revolution! Connect with innovators, get insider updates, and earn exciting rewards just for joining us.\n\nDon't miss out—become a community member today!`}
          </Typography>
        </Stack>
        <HexagonalButton width={140} label={'JOIN NOW'} onClick={openDiscord} />
      </Stack>
    </Modal>
  );
});

export default DiscordModal;
